import React, { useState, useEffect, useCallback } from "react";
import { IQWatchDevice, IQWatchDeviceWithData, Site } from "../types";
import {
  getIQWatchDevicesBySite,
  syncAllDevices,
} from "../services/iqwatchService.ts";
import { Plus, Edit, Trash2, Zap, Sun, RefreshCw } from "lucide-react";
import batteryEmptyIcon from "../assets/battery-empty-svgrepo-com.svg";
import batteryLowIcon from "../assets/battery-low-svgrepo-com.svg";
import batteryMidIcon from "../assets/battery-mid-svgrepo-com.svg";
import batteryFullIcon from "../assets/battery-full-svgrepo-com.svg";
import Pagination from "./Pagination.tsx";

interface IQWatchDeviceListProps {
  site: Site | null;
  orgId?: string;
  onEditDevice?: (device: IQWatchDevice) => void;
  onDeleteDevice?: (device: IQWatchDevice) => void;
  onAddDevice?: () => void;
}

const IQWatchDeviceList: React.FC<IQWatchDeviceListProps> = ({
  site,
  orgId,
  onEditDevice,
  onDeleteDevice,
  onAddDevice,
}) => {
  const [iqwatchDevices, setIqwatchDevices] = useState<IQWatchDeviceWithData[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const ITEMS_PER_PAGE = 10;

  // 获取 设备数据
  const fetchIQWatchDevices = useCallback(
    async (page: number = 1) => {
      if (!site || !orgId) {
        setIqwatchDevices([]);
        setPagination(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await getIQWatchDevicesBySite(
          site.id,
          orgId,
          page,
          ITEMS_PER_PAGE
        );
        setIqwatchDevices(response.devices || []);
        setPagination(response.pagination || null);
      } catch (error) {
        console.error("Failed to fetch devices:", error);
        setError("Failed to load devices");
      } finally {
        setLoading(false);
      }
    },
    [site, orgId]
  );

  useEffect(() => {
    setCurrentPage(1);
    fetchIQWatchDevices(1);
  }, [site, orgId, fetchIQWatchDevices]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchIQWatchDevices(page);
  };

  // 同步所有设备状态
  const handleSyncAllDevices = async () => {
    if (!orgId) {
      setSyncMessage("Organization ID is required for sync");
      setTimeout(() => setSyncMessage(null), 3000);
      return;
    }

    setSyncing(true);
    setSyncMessage(null);
    setError(null);

    try {
      const result = await syncAllDevices(orgId);
      setSyncMessage(
        `Sync completed: ${result.success} successful, ${result.failed} failed out of ${result.total} devices`
      );

      // Refresh the device list after sync
      await fetchIQWatchDevices(currentPage);

      // Clear success message after 5 seconds
      setTimeout(() => setSyncMessage(null), 5000);
    } catch (error) {
      console.error("Failed to sync devices:", error);
      setError("Failed to sync devices. Please try again.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setSyncing(false);
    }
  };

  // Add missing utility functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "bg-green-100 text-green-600";
      case "stopped":
        return "bg-yellow-100 text-yellow-600";
      case "error":
        return "bg-red-100 text-red-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const getBatterySocColor = (soc: number | string | undefined): string => {
    const socValue = typeof soc === "string" ? parseFloat(soc) : soc || 0;

    if (socValue >= 0 && socValue < 30) {
      return "text-red-600";
    } else if (socValue >= 30 && socValue < 60) {
      return "text-amber-600";
    } else if (socValue >= 60 && socValue <= 100) {
      return "text-green-600";
    }
    return "text-gray-600";
  };

  const getBatteryIcon = (soc: number | string | undefined) => {
    const socValue = typeof soc === "string" ? parseFloat(soc) : soc || 0;
    const color = getBatterySocColor(soc);

    // Get the appropriate icon based on SOC level
    let iconSrc: string;
    if (socValue >= 0 && socValue < 30) {
      iconSrc = batteryLowIcon;
    } else if (socValue >= 30 && socValue < 60) {
      iconSrc = batteryMidIcon;
    } else if (socValue >= 60 && socValue <= 100) {
      iconSrc = batteryFullIcon;
    } else {
      iconSrc = batteryEmptyIcon;
    }

    // Convert color class to hex for CSS filter
    const getColorFilter = (colorClass: string) => {
      if (colorClass === "text-red-600") {
        return "invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)";
      } else if (colorClass === "text-amber-600") {
        return "invert(70%) sepia(88%) saturate(1234%) hue-rotate(1deg) brightness(102%) contrast(95%)";
      } else if (colorClass === "text-green-600") {
        return "invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)";
      }
      return "none";
    };

    return (
      <img
        src={iconSrc}
        alt="battery"
        className={`w-5 h-5 ${color}`}
        style={{ filter: getColorFilter(color) }}
      />
    );
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading devices...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Devices</h3>
          <p className="text-sm text-gray-600">
            {site ? `Site: ${site.name}` : "No site selected"}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleSyncAllDevices}
            disabled={syncing || !orgId}
            className={`inline-flex items-center px-3 py-2 rounded-lg transition-colors ${
              syncing || !orgId
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
            title="Sync latest status for all devices"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`}
            />
            {syncing ? "Syncing..." : "Sync Latest Status"}
          </button>
          {onAddDevice && site && (
            <button
              onClick={onAddDevice}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Device
            </button>
          )}
        </div>
      </div>

      {/* Sync Message */}
      {syncMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {syncMessage}
        </div>
      )}

      {/* Devices List */}
      {iqwatchDevices.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600">No devices found.</p>
          {onAddDevice && (
            <button
              onClick={onAddDevice}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              Add the first device
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Device
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Battery
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Power
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Data
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {iqwatchDevices.map((deviceWithData) => {
                    const device = deviceWithData.device;
                    const data = deviceWithData.latest_data;

                    return (
                      <tr key={device.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {device.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {device.iqwatch_device_id}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                              data?.status || device.status
                            )}`}
                          >
                            {data?.status || device.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {getBatteryIcon(data?.soc || device.last_soc)}
                            <div>
                              <div
                                className={`text-sm font-medium ${getBatterySocColor(
                                  data?.soc || device.last_soc
                                )}`}
                              >
                                {data?.soc || device.last_soc}%
                              </div>
                              <div className="text-xs text-gray-500">
                                {data?.battery_voltage ||
                                  device.last_battery_voltage}
                                V
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-1">
                              <Sun className="w-3 h-3 text-yellow-500" />
                              <span className="text-xs">
                                {data?.solar_power || device.last_solar_power}W
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Zap className="w-3 h-3 text-blue-500" />
                              <span className="text-xs">
                                {data?.load_power || device.last_load_power}W
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDateTime(device.last_data_timestamp)}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            {onEditDevice && (
                              <button
                                onClick={() => onEditDevice(device)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit Device"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            {onDeleteDevice && (
                              <button
                                onClick={() => onDeleteDevice(device)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete Device"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.total_pages}
                onPageChange={handlePageChange}
                hasNext={pagination.has_next}
                hasPrev={pagination.has_prev}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default IQWatchDeviceList;
